import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Send, Download, Save } from 'lucide-react';
import { maintenanceService } from '../../services/maintenanceService';
import { supabase } from '../../services/supabaseService';
import type { MaintenanceContract, MaintenanceReport, MaintenanceTask } from '../../types/maintenance';
import ChecklistSummary from './ChecklistSummary';
import HoursProgressBar from './HoursProgressBar';

const TASK_TYPE_LABELS: Record<string, string> = {
  bug_fix: 'Bug',
  improvement: 'Mejora',
  new_feature: 'Nueva func.',
  support: 'Soporte',
  security: 'Seguridad',
  server: 'Servidor',
};

interface MonthlyReportViewProps {
  contract: MaintenanceContract;
  reportMonth: string; // 'YYYY-MM'
  onBack: () => void;
}

const MonthlyReportView: React.FC<MonthlyReportViewProps> = ({ contract, reportMonth, onBack }) => {
  const [report, setReport] = useState<MaintenanceReport | null>(null);
  const [tasks, setTasks] = useState<MaintenanceTask[]>([]);
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const reportRef = useRef<HTMLDivElement>(null);

  const monthLabel = new Date(reportMonth + '-01').toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  useEffect(() => {
    loadReport();
  }, [contract.id, reportMonth]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const r = await maintenanceService.getOrCreateMonthlyReport(contract.id, reportMonth);
      setReport(r);
      setTasks(r.tasks_snapshot || []);
      setSummary(r.summary || '');
    } catch (err) {
      setError((err as Error).message || 'No se pudo cargar el informe');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSummary = async () => {
    if (!report || report.finalized_at || report.status === 'sent') return;
    setSaving(true);
    try {
      const updated = await maintenanceService.updateReport(report.id, { summary });
      setReport(updated);
    } catch (err) { setError((err as Error).message); } finally {
      setSaving(false);
    }
  };

  const handleSendReport = async () => {
    if (!report || !report.finalized_at || saving) return;
    if (!confirm('¿Enviar este informe al cliente por email? Se marcará como enviado si el envío tiene éxito.')) return;
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-maintenance-report', {
        body: { report_id: report.id },
      });
      if (error) throw error;
      if ((data as any)?.ok === false) {
        alert(`No se pudo enviar el informe: ${(data as any).error || 'error desconocido'}`);
        return;
      }
      // Recargar para reflejar status='sent'
      await loadReport();
      alert(`Informe enviado a ${(data as any)?.sent_to || 'cliente'}.`);
    } catch (err: any) {
      setError(`Error enviando informe: ${err?.message || err}`);
    } finally { setSaving(false); }
  };

  const handleFinalize = async () => {
    if (!report || saving || !confirm('¿Cerrar este periodo? Las horas y el informe quedarán bloqueados.')) return;
    setSaving(true); setError('');
    try {
      await maintenanceService.updateReport(report.id, { summary });
      const closed = await maintenanceService.finalizeReport(contract.id, reportMonth);
      setReport(closed); setTasks(closed.tasks_snapshot || []);
    } catch (err) { setError((err as Error).message); }
    finally { setSaving(false); }
  };

  const handleDownloadPdf = async () => {
    if (!reportRef.current) return;
    const html2pdf = (await import('html2pdf.js')).default;
    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `informe-${contract.client_name}-${reportMonth}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(reportRef.current)
      .save();
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Cargando informe...</div>;
  }

  if (!report) {
    return <div className="py-12"><button onClick={onBack}>Volver</button><p role="alert" className="text-red-600">{error || 'Error cargando informe'}</p></div>;
  }

  const displayContract = { ...contract, ...report.contract_snapshot };
  return (
    <div>
      {error && <p role="alert" className="p-3 bg-red-50 text-red-700">{error}</p>}
      <p className="mb-4 text-sm text-gray-600">Las horas no utilizadas caducan al cierre del mes. No se acumulan.</p>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSaveSummary}
            disabled={saving || !!report.finalized_at || report.status === 'sent'}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Save className="w-4 h-4" />
            Guardar resumen
          </button>
          <button
            onClick={handleDownloadPdf}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download className="w-4 h-4" />
            Descargar PDF
          </button>
          {!report.finalized_at && report.status !== 'sent' && <button disabled={saving} onClick={handleFinalize} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white">Cerrar periodo</button>}
          {report.finalized_at && report.status !== 'sent' && (
            <button
              onClick={handleSendReport}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Send className="w-4 h-4" />
              Enviar por email
            </button>
          )}
        </div>
      </div>

      {report.status === 'sent' && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          Informe enviado el {new Date(report.sent_at!).toLocaleDateString('es-ES')}
        </div>
      )}

      {/* Report content (PDF target) */}
      <div ref={reportRef} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Informe de Mantenimiento</h2>
          <p className="text-lg text-gray-600">{displayContract.client_name} — {monthLabel}</p>
          <p className="text-sm text-gray-500 mt-1">{displayContract.plan_name} | {report.included_hours}h incluidas/mes</p>
        </div>

        {/* Summary metrics */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{Number(report.total_hours_used).toFixed(2)}h</p>
            <p className="text-sm text-blue-600">Total usadas</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-gray-700">{Number(report.support_hours_used).toFixed(2)}h</p>
            <p className="text-sm text-gray-600">Soporte</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-purple-700">{Number(report.improvement_hours_used).toFixed(2)}h</p>
            <p className="text-sm text-purple-600">Mejoras</p>
          </div>
          <div className={`rounded-lg p-4 text-center ${Number(report.extra_hours) > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
            <p className={`text-2xl font-bold ${Number(report.extra_hours) > 0 ? 'text-red-700' : 'text-green-700'}`}>
              {Number(report.extra_hours) > 0 ? `+${Number(report.extra_hours).toFixed(2)}h` : `${(report.included_hours - Number(report.total_hours_used)).toFixed(2)}h`}
            </p>
            <p className={`text-sm ${Number(report.extra_hours) > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {Number(report.extra_hours) > 0 ? `Extra (${Number(report.extra_hours_cost).toFixed(0)}€)` : (report.finalized_at ? 'Caducadas' : 'Disponibles')}
            </p>
          </div>
        </div>

        <div className="mb-8">
          <HoursProgressBar
            used={Number(report.total_hours_used)}
            included={report.included_hours}
            extra_price={displayContract.extra_hour_price}
          />
        </div>

        <ChecklistSummary items={report.checklist_snapshot || []} />

        {/* Tasks list */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalle de tareas ({tasks.length})</h3>
          {tasks.length === 0 ? (
            <p className="text-gray-500">No hay tareas registradas este mes</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 font-medium text-gray-600">Fecha</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-600">Tarea</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-600">Tipo</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-600">Horas</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id} className="border-b border-gray-100">
                    <td className="py-2 px-2 text-gray-600">{task.task_date}</td>
                    <td className="py-2 px-2">
                      <p className="font-medium text-gray-900">{task.title}</p>
                      {task.description && (
                        <p className="text-gray-500 text-xs mt-0.5">{task.description}</p>
                      )}
                    </td>
                    <td className="py-2 px-2 text-gray-600">{TASK_TYPE_LABELS[task.task_type]}</td>
                    <td className="py-2 px-2 text-right font-mono">{Number(task.hours).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td colSpan={3} className="py-2 px-2 font-semibold">Total</td>
                  <td className="py-2 px-2 text-right font-mono font-semibold">
                    {tasks.reduce((s, t) => s + Number(t.hours), 0).toFixed(2)}h
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Summary text */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Resumen del mes</h3>
          <textarea
            readOnly={!!report.finalized_at || report.status === 'sent'}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={4}
            placeholder="Escribe un resumen narrativo de la actividad del mes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>
    </div>
  );
};

export default MonthlyReportView;
